// Safe arithmetic expression evaluator for EstimateCostFormulas.Expression — quantity
// formulas an admin edits in the Electrical Assembly Library (db/29). This is
// deliberately NOT eval()/Function(): a database-stored string is untrusted
// input, and running it as JS would be a code-injection vector. Instead this
// is a hand-rolled recursive-descent parser that only understands numeric
// literals, +, -, *, /, parentheses, identifiers resolved from a fixed
// `variables` object passed in by the caller, and calls to the fixed
// FORMULA_FUNCTIONS whitelist below (ceil / ceiling / floor / round / abs / min / max) —
// no property access, no string/boolean operators, nothing else.

// The only callable names — a fixed whitelist of pure rounding / comparison helpers (2026-10-18, for assembly component
// formulas such as ceil(qty / 10)). Any other `name(` is an error, so a stored string still cannot reach arbitrary code.
// Rounding that ignores floating-point noise: 3.0000000001 rounds UP to 3, not 4 (a formula like ceiling(a / b, 1) must not
// jump a whole unit because a division came out a hair over).
const snap = (v) => +Number(v).toFixed(9);
const sigOf = (sig) => {
  if (!(sig > 0)) throw new Error("the significance (second value) must be greater than zero");
  return sig;
};
// ceiling / floor / round follow Excel: CEILING(x, significance), FLOOR(x, significance), ROUND(x, digits); the second value is optional.
export const FORMULA_FUNCTIONS = {
  ceil: { min: 1, max: 1, fn: (x) => Math.ceil(snap(x)) },
  ceiling: { min: 1, max: 2, fn: (x, sig = 1) => snap(Math.ceil(snap(x / sigOf(sig))) * sig) },
  floor: { min: 1, max: 2, fn: (x, sig = 1) => snap(Math.floor(snap(x / sigOf(sig))) * sig) },
  round: { min: 1, max: 2, fn: (x, digits = 0) => { const f = 10 ** Math.trunc(digits); return (Math.sign(x) * Math.round(snap(Math.abs(x) * f))) / f; } },
  abs: { min: 1, max: 1, fn: Math.abs },
  min: { min: 1, max: 8, fn: Math.min },
  max: { min: 1, max: 8, fn: Math.max },
};

const TOKEN_RE = /\s*([0-9]*\.?[0-9]+|[A-Za-z_][A-Za-z0-9_]*|[+\-*/(),])/y;

function tokenize(expr) {
  const tokens = [];
  TOKEN_RE.lastIndex = 0;
  let lastIndex = 0;
  let match;
  while ((match = TOKEN_RE.exec(expr))) {
    tokens.push(match[1]);
    lastIndex = TOKEN_RE.lastIndex;
    if (TOKEN_RE.lastIndex === match.index) break; // safety against zero-length match loops
  }
  if (lastIndex !== expr.length) {
    throw new Error(`Unexpected character at position ${lastIndex} in formula: ${expr}`);
  }
  return tokens;
}

// Throws on any parse error, unknown variable, or division by zero — callers
// should prefer tryEvaluateFormula() and fall back to a hardcoded default.
// Names are forgiving the way a spreadsheet is: function names ignore case (CEILING = ceiling), and a variable typed in the wrong
// case still resolves when exactly one variable matches ignoring case (roomvolume -> roomVolume). An exact match always wins, and
// two variables that differ only by case stay ambiguous (an error), so nothing is ever bound to the wrong one silently.
export function resolveName(name, names) {
  const list = names instanceof Set ? [...names] : Array.isArray(names) ? names : Object.keys(names || {});
  if (list.includes(name)) return name;
  const matches = list.filter(n => n.toLowerCase() === name.toLowerCase());
  return matches.length === 1 ? matches[0] : null;
}
export const functionSpec = (name) => {
  const key = Object.keys(FORMULA_FUNCTIONS).find(k => k === name) ?? Object.keys(FORMULA_FUNCTIONS).find(k => k === String(name).toLowerCase());
  return key ? FORMULA_FUNCTIONS[key] : null;
};

export function evaluateFormula(expression, variables = {}) {
  const tokens = tokenize(String(expression ?? "").replace(/^\s*=/, "")); // a leading = is allowed, as in a spreadsheet
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = () => tokens[pos++];

  function parseExpression() {
    let value = parseTerm();
    while (peek() === "+" || peek() === "-") {
      const op = consume();
      const rhs = parseTerm();
      value = op === "+" ? value + rhs : value - rhs;
    }
    return value;
  }
  function parseTerm() {
    let value = parseFactor();
    while (peek() === "*" || peek() === "/") {
      const op = consume();
      const rhs = parseFactor();
      if (op === "/") {
        if (rhs === 0) throw new Error("Division by zero in formula");
        value = value / rhs;
      } else {
        value = value * rhs;
      }
    }
    return value;
  }
  function parseFactor() {
    const tok = peek();
    if (tok === undefined) throw new Error("Unexpected end of formula");
    if (tok === "+") { consume(); return parseFactor(); }
    if (tok === "-") { consume(); return -parseFactor(); }
    if (tok === "(") {
      consume();
      const value = parseExpression();
      if (consume() !== ")") throw new Error("Mismatched parentheses in formula");
      return value;
    }
    if (/^[0-9.]/.test(tok)) { consume(); return parseFloat(tok); }
    if (/^[A-Za-z_]/.test(tok)) {
      consume();
      if (peek() === "(") {
        const spec = functionSpec(tok);
        if (!spec) throw new Error(`Unknown function "${tok}" in formula`);
        consume(); // (
        const args = [parseExpression()];
        while (peek() === ",") { consume(); args.push(parseExpression()); }
        if (consume() !== ")") throw new Error("Mismatched parentheses in formula");
        if (args.length < spec.min || args.length > spec.max) throw new Error(`${tok}() takes ${spec.min === spec.max ? spec.min : `${spec.min}–${spec.max}`} value${spec.max === 1 ? "" : "s"}`);
        return spec.fn(...args);
      }
      const name = resolveName(tok, variables);
      if (name == null) throw new Error(`Unknown variable "${tok}" in formula`);
      const v = variables[name];
      if (typeof v !== "number" || Number.isNaN(v)) throw new Error(`Variable "${tok}" is not a number`);
      return v;
    }
    throw new Error(`Unexpected token "${tok}" in formula`);
  }

  const result = parseExpression();
  if (pos !== tokens.length) throw new Error(`Unexpected token "${tokens[pos]}" in formula`);
  return result;
}

// Never throws — returns null on any parse/eval problem so callers fall back
// to their hardcoded default instead of breaking the estimate.
export function tryEvaluateFormula(expression, variables) {
  if (!expression) return null;
  try { return evaluateFormula(expression, variables); }
  catch { return null; }
}
